import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, CircularProgress, Alert, Checkbox, Chip,
  TableSortLabel, TextField
} from '@mui/material';
import { CloudUpload, Delete, ListAlt, DeleteSweep, EventNote } from '@mui/icons-material';
import axios from 'axios';

type Order = 'asc' | 'desc';

const OrdersPage: React.FC = () => {
  const [periods, setPeriods] = useState<any[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(0);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  // Фильтр, сортировка и выделение
  const [filterDate, setFilterDate] = useState('');
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>('date');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    axios.get('/api/periods').then(res => {
      setPeriods(res.data);
      const active = res.data.find((p: any) => p.is_active === 1);
      if (active) setSelectedPeriod(active.id);
    });
  }, []);

  useEffect(() => {
    if (selectedPeriod) fetchOrders(selectedPeriod);
  }, [selectedPeriod]);

  const fetchOrders = async (periodId: number) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/orders/${periodId}`);
      setOrders(res.data);
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`/api/orders/upload/${selectedPeriod}`, formData);
      setMessage(`Успешно загружено ${res.data.added} заказов`);
      fetchOrders(selectedPeriod);
    } catch (err) {
      setMessage('Ошибка загрузки файла');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить заказ?')) return;
    try {
      await axios.delete(`/api/orders/${id}`);
      fetchOrders(selectedPeriod);
    } catch (err) {
      alert('Ошибка удаления');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Удалить выбранные заказы (${selectedIds.length} шт.)?`)) return;
    try {
      await axios.post('/api/orders/delete-bulk', { ids: selectedIds });
      fetchOrders(selectedPeriod);
    } catch (err) {
      alert('Ошибка массового удаления');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('ВНИМАНИЕ! Удалить ВСЕ заказы за выбранный период?')) return;
    try {
      await axios.delete(`/api/orders/clear/${selectedPeriod}`);
      fetchOrders(selectedPeriod);
      setMessage('Все заказы периода удалены');
    } catch (err) {
      alert('Ошибка очистки');
    }
  };

  // --- ЛОГИКА СОРТИРОВКИ И ФИЛЬТРАЦИИ ---
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredOrders = orders.filter(item => {
    if (!filterDate) return true;
    // Берем первые 10 символов (YYYY-MM-DD)
    const itemDate = (item.date || '').substring(0, 10);
    return itemDate === filterDate;
  });

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    let valA = a[orderBy] || '';
    let valB = b[orderBy] || '';
    
    if (orderBy === 'date') {
       const dateCompare = a.date.localeCompare(b.date);
       if (dateCompare !== 0) return order === 'asc' ? dateCompare : -dateCompare;
       return order === 'asc' ? (a.time || '').localeCompare(b.time || '') : (b.time || '').localeCompare(a.time || '');
    }

    if (order === 'asc') return valA < valB ? -1 : 1;
    return valA > valB ? -1 : 1;
  });

  // --- МНОЖЕСТВЕННЫЙ ВЫБОР ---
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(sortedOrders.map(o => o.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: number) => {
    if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(item => item !== id));
    else setSelectedIds([...selectedIds, id]);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <ListAlt sx={{ fontSize: 32, color: 'primary.main' }} />
        
        <Chip label={`Всего: ${orders.length}`} color="primary" variant="outlined" sx={{ ml: 1, fontWeight: 600 }} />
        {filterDate && <Chip label={`На дату: ${filteredOrders.length}`} color="secondary" variant="outlined" sx={{ ml: 1, fontWeight: 600 }} />}
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Период</InputLabel>
            <Select value={selectedPeriod} label="Период" onChange={(e) => setSelectedPeriod(Number(e.target.value))}>
              {periods.map(p => <MenuItem key={p.id} value={p.id}>{p.month_name} {p.year}</MenuItem>)}
            </Select>
          </FormControl>

          <Button variant="contained" component="label" startIcon={<CloudUpload />} disabled={uploading || !selectedPeriod}>
            {uploading ? 'Загрузка...' : 'Загрузить Excel'}
            <input type="file" hidden accept=".xlsx, .xls" onChange={handleFileUpload} />
          </Button>

          <TextField
            type="date"
            size="small"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            sx={{ width: 160 }}
            InputProps={{
              startAdornment: <Box sx={{ color: 'text.secondary', mr: 1, display: 'flex' }}><EventNote fontSize="small" /></Box>
            }}
          />

          <Box sx={{ flexGrow: 1 }} />

          {selectedIds.length > 0 && (
            <Button variant="contained" color="error" startIcon={<DeleteSweep />} onClick={handleBulkDelete}>
              Удалить выбранные ({selectedIds.length})
            </Button>
          )}

          <Button variant="outlined" color="error" startIcon={<Delete />} onClick={handleClearAll}>
            Очистить всё
          </Button>
        </Box>
      </Paper>

      {message && <Alert severity={message.includes('Ошибка') ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.length > 0 && selectedIds.length < sortedOrders.length}
                    checked={sortedOrders.length > 0 && selectedIds.length === sortedOrders.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel active={orderBy === 'date'} direction={orderBy === 'date' ? order : 'asc'} onClick={() => handleRequestSort('date')}>
                    Дата / Время
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={orderBy === 'tour_name'} direction={orderBy === 'tour_name' ? order : 'asc'} onClick={() => handleRequestSort('tour_name')}>
                    Экскурсия
                  </TableSortLabel>
                </TableCell>
                <TableCell>Программа экскурсии</TableCell>
                <TableCell>
                  <TableSortLabel active={orderBy === 'customer'} direction={orderBy === 'customer' ? order : 'asc'} onClick={() => handleRequestSort('customer')}>
                    Заказчик
                  </TableSortLabel>
                </TableCell>
                <TableCell>Класс</TableCell>
                <TableCell>Язык</TableCell>
                <TableCell align="center">Кол-во</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    {filterDate ? 'На эту дату заказов нет' : 'Нет заказов на этот период'}
                  </TableCell>
                </TableRow>
              ) : (
                sortedOrders.map((row) => (
                  <TableRow key={row.id} hover selected={selectedIds.includes(row.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedIds.includes(row.id)} onChange={() => handleSelectOne(row.id)} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" fontWeight={500}>
                          {/* ТУТ МЫ БЕРЕМ СТРОКУ "2026-10-21" И РАЗБИВАЕМ ПО ДЕФИСУ */}
                          {(() => {
                            const rawDate = String(row.date || '');
                            const cleanDate = rawDate.split('T')[0]; // на случай если пришло с временем
                            const parts = cleanDate.split('-');
                            if (parts.length === 3) {
                              return `${parts[2]}.${parts[1]}`; // Возвращаем "21.10"
                            }
                            return rawDate;
                          })()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">{row.time?.slice(0, 5) || '--:--'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 250 }}>{row.tour_name}</TableCell>
                    <TableCell sx={{ maxWidth: 200, fontSize: '0.8rem', color: 'text.secondary' }}>{row.program_name || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.customer || ''}>
                      {row.customer || '-'}
                    </TableCell>
                    <TableCell>{row.class_name || '-'}</TableCell>
                    <TableCell>{row.is_english ? '🇬🇧' : '🇷🇺'}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{row.people_count || 0}</TableCell>
                    <TableCell align="right">
                      <IconButton color="error" onClick={() => handleDelete(row.id)}><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default OrdersPage;
